import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { XMarkIcon } from '@heroicons/react/24/solid';

import { useDispatch, useSelector } from 'hooks';
import { isBrowserSupported } from 'utils/browserSupport';
import { dismissUnsupportedBrowserBanner } from 'modules/app/actions';
import { isUnsupportedBrowserBannerDismissed } from 'modules/app/selectors';

// Inline styles, not Tailwind: the browsers that see this banner ignore part or all of the Tailwind v4 CSS. The colors are
// the design tokens, with their value as fallback: the oldest browsers also ignore the tokens (declared in @layer base)
const BANNER_STYLE: React.CSSProperties = {
  position: 'relative',
  zIndex: 100,
  padding: '12px 48px',
  backgroundColor: 'var(--color-warning-lowest, #feefd0)',
  color: 'var(--color-default, #1d252d)',
  font: '16px/1.4 system-ui, sans-serif',
  textAlign: 'center',
};

const CLOSE_BUTTON_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '50%',
  right: '8px',
  transform: 'translateY(-50%)',
  display: 'flex',
  padding: '8px',
  border: 0,
  background: 'none',
  color: 'inherit',
  cursor: 'pointer',
};

const CLOSE_ICON_STYLE: React.CSSProperties = { width: '20px', height: '20px' };

const UnsupportedBrowserBanner: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isDismissed = useSelector(isUnsupportedBrowserBannerDismissed);
  const isSupported = React.useMemo(() => isBrowserSupported(), []);
  const handleClose = React.useCallback(() => dispatch(dismissUnsupportedBrowserBanner()), [dispatch]);

  return !isSupported && !isDismissed && (
    <div role="alert" style={BANNER_STYLE}>
      <FormattedMessage
        id="error.unsupportedBrowser.description"
        defaultMessage="This browser is not supported: some screens may not display correctly. Please update your device or use another browser."
      />
      <button
        type="button"
        style={CLOSE_BUTTON_STYLE}
        aria-label={intl.formatMessage({ id: 'close', defaultMessage: 'Close' })}
        onClick={handleClose}
      >
        <XMarkIcon style={CLOSE_ICON_STYLE} aria-hidden />
      </button>
    </div>
  );
};

export default UnsupportedBrowserBanner;
